import React from 'react';
import { deviceMapsApp, jobDirectionsUrl } from '../utils/jobAddress';
import { useToasts } from './ToastProvider';
import './JobAddressLink.css';

// Keep address navigation independent of card opening, selection and calendar dragging.
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

// What a tap does on this device, for the link's accessible name.
const OPENS = { apple: 'opens Apple Maps', default: 'opens your maps app', google: 'opens Google Maps in a new tab' } as const;

export default function JobAddressLink({ address, lines, label, compactText = 'Directions', compact = false, className = '' }: {
  address: string | null | undefined;
  lines?: string[];
  label?: string;
  compactText?: string;
  compact?: boolean;
  className?: string;
}) {
  const value = address?.trim() ?? '';
  const app = deviceMapsApp();
  const href = jobDirectionsUrl(value, app);
  if (!href) return <span className="job-address-empty">Address not set</span>;
  // A geo: link hands off to the phone's maps app, so a new tab would only be left behind empty.
  const newTab = app !== 'default';
  // A multi-line address sits under its place's name: no pin, so name and street share one left
  // edge, and the arrow follows the last word instead of floating beside the block.
  const blockLines = !compact && lines?.length ? lines : null;
  const external = <span className="job-address-link__external" aria-hidden="true">↗</span>;
  return (
    <a className={`job-address-link${compact ? ' job-address-link--compact' : ''}${blockLines ? ' job-address-link--lines' : ''} ${className}`}
      href={href} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined} draggable={false}
      aria-label={`Get directions${label ? ` to ${label}` : ''}: ${value} (${OPENS[app]})`}
      title={`Get directions: ${value}`} onClick={stop} onPointerDown={stop} onMouseDown={stop}
      onTouchStart={stop} onDoubleClick={stop}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }}>
      {blockLines ? null : (
        <svg className="job-address-link__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" />
        </svg>
      )}
      <span className="job-address-link__text">
        {blockLines
          ? blockLines.map((line, index) => <span key={index}>{line}{index === blockLines.length - 1 ? external : null}</span>)
          : compact ? compactText : value}
      </span>
      {blockLines ? null : external}
    </a>
  );
}

// The button itself confirms a copy for this long; only a failure raises a toast.
const COPIED_MS = 2000;

export function JobAddressCopyButton({ address, label }: { address: string; label?: string }) {
  const { push } = useToasts();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(timer.current), []);
  return <>
    <button type="button" className={`btn btn-secondary job-address-copy${copied ? ' job-address-copy--copied' : ''}`}
      aria-label={label ? `Copy address for ${label}` : 'Copy address'} onPointerDown={stop} onClick={async event => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(address);
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
        } catch {
          push({ type: 'warn', message: 'Your browser could not copy the address. Select the address text and copy it instead.' });
        }
      }}>
      <svg className="job-address-copy__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        {copied ? <path d="m5 12.5 4.5 4.5L19 7.5" /> : <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>}
      </svg>
      {copied ? 'Copied' : 'Copy'}
    </button>
    <span className="sr-only" role="status">{copied ? 'Address copied' : ''}</span>
  </>;
}
